import { Link } from 'react-router-dom';
import { Github, Twitter, Linkedin, Instagram, Send } from 'lucide-react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { Container } from '../ui/Container';
import AdContainer from '../ads/AdContainer';
import Spinner from '../ui/Spinner';
import { useNewsletterSubscribe } from '../../hooks/useNewsletterSubscribe';

const FooterWrapper = styled.footer`
  border-top: 1px solid ${props => props.theme.colors.border};
  background-color: ${props => props.theme.colors.card};
  color: ${props => props.theme.colors.cardForeground};
  margin-top: 5rem;
`;

const FooterContent = styled(Container)`
  padding-top: 3rem;
  padding-bottom: 3rem;
  
  @media (min-width: 768px) {
    padding-top: 4rem;
    padding-bottom: 4rem;
  }
  
  @media (min-width: 1024px) {
    padding-top: 5rem;
    padding-bottom: 5rem;
  }
  
  @media (min-width: 1440px) {
    padding-top: 6rem;
    padding-bottom: 6rem;
  }
`;

const FooterGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  margin-bottom: 3rem;
  
  @media (min-width: 768px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 2.5rem;
    margin-bottom: 4rem;
  }
  
  @media (min-width: 1024px) {
    gap: 3rem;
    margin-bottom: 5rem;
  }
  
  @media (min-width: 1440px) {
    gap: 4rem;
  }
`;

const BrandSection = styled.div`
  grid-column: span 2;
  
  @media (min-width: 768px) {
    grid-column: span 1;
  }
`;

const BrandHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const LogoBox = styled.div`
  width: 2.5rem;
  height: 2.5rem;
  background-color: ${props => props.theme.colors.primary};
  border-radius: ${props => props.theme.borderRadius['2xl']};
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (min-width: 1024px) {
    width: 3rem;
    height: 3rem;
  }
  
  @media (min-width: 1440px) {
    width: 3.5rem;
    height: 3.5rem;
  }
`;

const LogoLetter = styled.span`
  color: ${props => props.theme.colors.primaryForeground};
  font-family: ${props => props.theme.typography.fonts.display};
  font-weight: ${props => props.theme.typography.fontWeights.bold};
  font-size: 1rem;
  
  @media (min-width: 640px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.375rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.5rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 1.625rem;
  }
`;

const LogoText = styled.span`
  font-family: ${props => props.theme.typography.fonts.display};
  font-size: 1.125rem;
  font-weight: ${props => props.theme.typography.fontWeights.semibold};
  
  @media (min-width: 640px) {
    font-size: 1.25rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1.375rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.5rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.625rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 1.75rem;
  }
`;

const BrandDescription = styled.p`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.mutedForeground};
  margin-bottom: 1rem;
  
  @media (min-width: 640px) {
    font-size: 0.9375rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.0625rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 1.25rem;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 0.75rem;
  
  @media (min-width: 1024px) {
    gap: 1rem;
  }
`;

const SocialLink = styled.a`
  padding: 0.5rem;
  border-radius: ${props => props.theme.borderRadius['2xl']};
  background-color: ${props => props.theme.colors.secondary};
  color: ${props => props.theme.colors.foreground};
  transition: background-color 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (min-width: 1024px) {
    padding: 0.625rem;
  }
  
  @media (min-width: 1440px) {
    padding: 0.75rem;
  }
  
  &:hover {
    background-color: ${props => props.theme.colors.muted};
  }
  
  svg {
    width: 1rem;
    height: 1rem;
    
    @media (min-width: 1024px) {
      width: 1.125rem;
      height: 1.125rem;
    }
    
    @media (min-width: 1440px) {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const StyledLink = styled(Link)`
  font-size: 0.875rem;
  color: ${props => props.theme.colors.mutedForeground};
  text-decoration: none;
  transition: color 0.2s;
  
  @media (min-width: 640px) {
    font-size: 0.9375rem;
  }
  
  @media (min-width: 768px) {
    font-size: 1rem;
  }
  
  @media (min-width: 1024px) {
    font-size: 1.0625rem;
  }
  
  @media (min-width: 1440px) {
    font-size: 1.125rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 1.25rem;
  }
  
  &:hover {
    color: ${props => props.theme.colors.foreground};
  }
`;

const LinkSection = styled.div`
  h4 {
    font-weight: 500;
    margin-bottom: 1rem;
    font-size: 1rem;
    
    @media (min-width: 640px) {
      font-size: 1.125rem;
    }
    
    @media (min-width: 768px) {
      font-size: 1.25rem;
    }
    
    @media (min-width: 1024px) {
      font-size: 1.375rem;
      margin-bottom: 1.25rem;
    }
    
    @media (min-width: 1440px) {
      font-size: 1.5rem;
      margin-bottom: 1.5rem;
    }
    
    @media (min-width: 1920px) {
      font-size: 1.625rem;
    }
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    
    @media (min-width: 1024px) {
      gap: 0.625rem;
    }
    
    @media (min-width: 1440px) {
      gap: 0.75rem;
    }
  }
`;

const FooterSubscribeForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin: 1rem 0 1.25rem;
  max-width: 20rem;
`;

const FooterEmailInput = styled.input`
  height: 2.5rem;
  padding: 0 0.75rem;
  border-radius: 8px;
  border: 1px solid ${(p) => p.theme.colors.border};
  background: ${(p) => p.theme.colors.background};
  color: ${(p) => p.theme.colors.foreground};
  font-size: 0.875rem;

  &:focus-visible {
    outline: none;
    border-color: ${(p) => p.theme.colors.primary};
  }
`;

const FooterSubscribeBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  height: 2.5rem;
  padding: 0 1rem;
  border: none;
  border-radius: 8px;
  background: ${(p) => p.theme.colors.primary};
  color: ${(p) => p.theme.colors.primaryForeground};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const FooterSubscribeNote = styled.p`
  margin: 0;
  font-size: 0.75rem;
  color: ${(p) =>
    p.$type === 'success' ? p.theme.colors.success : p.$type === 'error' ? p.theme.colors.error : p.theme.colors.mutedForeground};
`;

const FooterBottom = styled.div`
  padding-top: 2rem;
  border-top: 1px solid ${props => props.theme.colors.border};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  font-size: 0.875rem;
  color: ${props => props.theme.colors.mutedForeground};
  
  @media (min-width: 640px) {
    font-size: 0.9375rem;
  }
  
  @media (min-width: 768px) {
    flex-direction: row;
    font-size: 1rem;
  }
  
  @media (min-width: 1024px) {
    padding-top: 2.5rem;
    font-size: 1.0625rem;
  }
  
  @media (min-width: 1440px) {
    padding-top: 3rem;
    font-size: 1.125rem;
  }
  
  @media (min-width: 1920px) {
    font-size: 1.25rem;
  }
`;

const Footer = () => {
  const { t } = useTranslation();
  const { language, getCategoryUrl } = useLanguage();
  const currentYear = new Date().getFullYear();
  const { email, setEmail, isSubmitting, message, messageType, submit } = useNewsletterSubscribe();

  const handleFooterSubscribe = async (e) => {
    e.preventDefault();
    await submit();
  };
  
  return (
    <FooterWrapper>
      <FooterContent>
        {/* Footer Ad */}
        <AdContainer placement="footer" limit={1} />
        
        <FooterGrid>
          <BrandSection>
            <BrandHeader>
              <LogoBox>
                <LogoLetter>B</LogoLetter>
              </LogoBox>
              <LogoText>{t('site.name')}</LogoText>
            </BrandHeader>
            <BrandDescription>
              {t('newsletter.description')}
            </BrandDescription>
            <FooterSubscribeForm onSubmit={handleFooterSubscribe}>
              <FooterEmailInput
                type="email"
                placeholder={t('newsletter.placeholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isSubmitting}
                required
                aria-label={t('newsletter.placeholder')}
              />
              <FooterSubscribeBtn type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Spinner size="sm" /> : <Send size={16} />}
                {t('newsletter.button')}
              </FooterSubscribeBtn>
              {message ? (
                <FooterSubscribeNote $type={messageType}>{message}</FooterSubscribeNote>
              ) : null}
            </FooterSubscribeForm>
            <SocialLinks>
              <SocialLink href="https://twitter.com" target="_blank" rel="noopener noreferrer" aria-label="Twitter">
                <Twitter />
              </SocialLink>
              <SocialLink href="https://github.com" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
                <Github />
              </SocialLink>
              <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
                <Linkedin />
              </SocialLink>
              <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                <Instagram />
              </SocialLink>
            </SocialLinks>
          </BrandSection>

          <LinkSection>
            <h4>Explore</h4>
            <ul>
              <li><StyledLink to={language === 'en' ? '/' : `/${language}`}>{t('nav.home')}</StyledLink></li>
              <li><StyledLink to={language === 'en' ? '/trending' : `/${language}/trending`}>{t('nav.trending')}</StyledLink></li>
              <li><StyledLink to={language === 'en' ? '/categories' : `/${language}/categories`}>{t('nav.categories')}</StyledLink></li>
              <li><StyledLink to={language === 'en' ? '/authors' : `/${language}/authors`}>{t('nav.authors')}</StyledLink></li>
            </ul>
          </LinkSection>

          <LinkSection>
            <h4>{t('nav.categories')}</h4>
            <ul>
              <li><StyledLink to={getCategoryUrl('technology', language)}>Technology</StyledLink></li>
              <li><StyledLink to={getCategoryUrl('design', language)}>Design</StyledLink></li>
              <li><StyledLink to={getCategoryUrl('lifestyle', language)}>Lifestyle</StyledLink></li>
              <li><StyledLink to={getCategoryUrl('business', language)}>Business</StyledLink></li>
            </ul>
          </LinkSection>

          <LinkSection>
            <h4>Company</h4>
            <ul>
              <li><StyledLink to={language === 'en' ? '/about' : `/${language}/about`}>{t('footer.about')}</StyledLink></li>
              <li><StyledLink to={language === 'en' ? '/contact' : `/${language}/contact`}>{t('footer.contact')}</StyledLink></li>
              <li><StyledLink to={language === 'en' ? '/privacy' : `/${language}/privacy`}>{t('footer.privacy')}</StyledLink></li>
              <li><StyledLink to={language === 'en' ? '/terms' : `/${language}/terms`}>{t('footer.terms')}</StyledLink></li>
            </ul>
          </LinkSection>
        </FooterGrid>

        <FooterBottom>
          <p>{t('footer.copyright', { year: currentYear })}</p>
          <p>Made with passion for great content</p>
        </FooterBottom>
      </FooterContent>
    </FooterWrapper>
  );
};

export default Footer;

